// ─── Feed geser halaman produk ───────────────────────────────────────────────
//
// Pembeli yang sudah di foto TERAKHIR lalu menggeser lagi dibawa ke produk
// BERIKUTNYA (dan dari foto pertama ke produk SEBELUMNYA) — pengalaman belanja
// ala media sosial: terus menggeser tanpa kembali ke katalog.
//
// Aturan urutannya keputusan pemilik (2026-09-24), bukan selera:
//   1. Hanya produk bertipe SAMA dengan yang sedang dibuka (Pre-Order ↔
//      Pre-Order, Ready Stock ↔ Ready Stock) — cermin tab Beranda, supaya
//      reseller yang butuh barang cepat tak mendarat di PO.
//   2. Per KATEGORI: habiskan kategori produk ini dulu, lalu kategori
//      berikutnya menurut urutan master (A–Z, sama dengan tile Kategori);
//      setelah kategori terakhir berputar ke awal.
//   3. Dalam kategori: TERBARU dulu — urutan index katalog apa adanya.
//
// Sumbernya index katalog ringan (`getDiscoverableCatalogIndex`) yang sudah
// ber-cache per toko di server; modul ini murni supaya urutannya diuji di
// Vitest tanpa Supabase.

import {
  effectiveType,
  isDiscoverableProduct,
  type HomeCatalogItem,
  type HomeProductType,
} from "@/lib/homeCatalog";
import type { Product } from "@/lib/products";
import { buildCatalogProductPath } from "@/lib/resellerCatalog";
import { withFeedContext, type FeedContext } from "@/lib/feedContext";

export type FeedDirection = "next" | "prev";

/** Produk yang sedang dibuka — cukup tiga medan, dipenuhi `Product` maupun item index. */
export type FeedCurrent = Pick<HomeCatalogItem, "id" | "category" | "productType">;

export type FeedStepMeta = {
  direction: FeedDirection;
  /** Tipe feed (= tipe produk yang sedang dibuka). */
  type: HomeProductType;
  /** Label kategori produk yang sedang dibuka; `null` = tanpa kategori. */
  fromLabel: string | null;
  /** Label kategori produk tujuan; `null` = tanpa kategori. */
  toLabel: string | null;
  /** Tujuan ada di kategori LAIN — kategori produk ini sudah habis. */
  switchesCategory: boolean;
  /** Deret sudah habis dan feed berputar ke ujung yang lain. */
  restarts: boolean;
  /**
   * Daftar asal (`?dari=`, `feedContext.ts`) yang MASIH dijalani langkah ini —
   * tautannya ikut membawa param yang sama. `null` = feed kategori.
   */
  context: FeedContext | null;
  /** Nama daftar asal: untuk langkah di dalamnya, atau yang baru ditinggalkan. */
  contextLabel: string | null;
  /** Langkah ini KELUAR dari daftar asal (daftar habis) ke feed kategori. */
  leavesContext: boolean;
};

export type FeedStep<T> = FeedStepMeta & { item: T };

/** Salinan meta sebuah langkah TANPA item-nya (item index tak ikut ke klien). */
export function feedStepMeta(step: FeedStepMeta): FeedStepMeta {
  return {
    direction: step.direction,
    type: step.type,
    fromLabel: step.fromLabel,
    toLabel: step.toLabel,
    switchesCategory: step.switchesCategory,
    restarts: step.restarts,
    context: step.context,
    contextLabel: step.contextLabel,
    leavesContext: step.leavesContext,
  };
}

/** Satu langkah feed yang sudah dihidrasi jadi kartu (cover + harga perwakilan). */
export type ProductFeedStep = { product: Product; meta: FeedStepMeta };

/** Tetangga produk yang sedang dibuka; `null` = tak ada produk lain di arah itu. */
export type ProductFeed = {
  next: ProductFeedStep | null;
  prev: ProductFeedStep | null;
};

export const EMPTY_PRODUCT_FEED: ProductFeed = { next: null, prev: null };

/**
 * Kandidat per arah. Lebih dari satu karena produk ready stock bisa nonaktif
 * (stok habis → `is_active=false`) di dalam jendela cache index 60 dtk: bila
 * hanya satu kandidat dan ia gagal dihidrasi, pratinjaunya hilang diam-diam.
 */
export const FEED_CANDIDATES = 3;

type FeedGroup<T> = {
  /** Kunci pembanding kategori (trim + lowercase); "" = tanpa kategori. */
  key: string;
  label: string | null;
  items: T[];
};

// Cermin `normalize` di homeCatalog.ts: `products.category` cuma snapshot
// teks (bukan FK), jadi kapitalisasi/spasi bisa menyimpang dari master.
function categoryKey(value: string | undefined | null): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Kelompokkan produk discoverable bertipe `type` per kategori, dalam urutan
 * feed:
 *   1. kategori MASTER, urut master (dedupe beda huruf — yang pertama menang,
 *      labelnya ejaan master); master tanpa produk dilewati;
 *   2. kategori YATIM (teks produk yang tak ada di master — master diganti
 *      nama/dihapus), urut kuncinya. Pembanding `<` biasa, BUKAN
 *      `localeCompare`: hasilnya tak boleh bergantung pada ICU server;
 *   3. produk TANPA kategori paling akhir.
 * Di dalam grup urutan index dipertahankan (terbaru dulu).
 */
function buildFeedGroups<T extends HomeCatalogItem>(
  index: readonly T[],
  categoryMaster: readonly string[],
  type: HomeProductType
): FeedGroup<T>[] {
  const byKey = new Map<string, T[]>();
  const textLabel = new Map<string, string>();
  for (const item of index) {
    if (!isDiscoverableProduct(item) || effectiveType(item) !== type) continue;
    const key = categoryKey(item.category);
    const bucket = byKey.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      byKey.set(key, [item]);
      textLabel.set(key, (item.category ?? "").trim());
    }
  }

  const groups: FeedGroup<T>[] = [];
  const masterKeys = new Set<string>();
  for (const raw of categoryMaster) {
    const label = raw.trim();
    const key = label.toLowerCase();
    if (!key || masterKeys.has(key)) continue;
    masterKeys.add(key);
    const items = byKey.get(key);
    if (items?.length) groups.push({ key, label, items });
  }

  const orphanKeys = [...byKey.keys()]
    .filter((key) => key !== "" && !masterKeys.has(key))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  for (const key of orphanKeys) {
    groups.push({ key, label: textLabel.get(key) || null, items: byKey.get(key) ?? [] });
  }

  const uncategorized = byKey.get("");
  if (uncategorized?.length) groups.push({ key: "", label: null, items: uncategorized });
  return groups;
}

/** Label kategori produk yang sedang dibuka: grupnya, ejaan master, atau teksnya sendiri. */
function resolveFromLabel<T extends HomeCatalogItem>(
  groups: readonly FeedGroup<T>[],
  categoryMaster: readonly string[],
  current: FeedCurrent
): string | null {
  const key = categoryKey(current.category);
  const group = groups.find((candidate) => candidate.key === key);
  const masterLabel = categoryMaster
    .map((name) => name.trim())
    .find((name) => name && name.toLowerCase() === key);
  return group?.label ?? masterLabel ?? (current.category?.trim() || null);
}

/**
 * Deret feed lengkap untuk satu tipe — urutan yang sama dengan langkah
 * `productFeedNeighbors`, dipakai halaman yang menampilkan feed utuh.
 */
export function buildFeedSequence<T extends HomeCatalogItem>(
  index: readonly T[],
  categoryMaster: readonly string[],
  type: HomeProductType
): T[] {
  return buildFeedGroups(index, categoryMaster, type).flatMap((group) => group.items);
}

/**
 * Kandidat produk BERIKUTNYA & SEBELUMNYA dari `current` (masing-masing ≤
 * `limit`, unik, tanpa `current`). Deretnya melingkar: lewat ujung →
 * `restarts`.
 *
 * `current` yang TIDAK ada di deret (PO yang sudah tutup dibuka lewat link,
 * atau produk nonaktif) diperlakukan seolah berdiri tepat di depan grup
 * kategorinya: berikutnya = produk terbaru kategori itu, sebelumnya = ujung
 * grup sebelumnya. Kategorinya tak punya grup → berdiri di awal deret.
 */
export function productFeedNeighbors<T extends HomeCatalogItem>(
  index: readonly T[],
  categoryMaster: readonly string[],
  current: FeedCurrent,
  limit: number = FEED_CANDIDATES
): { next: FeedStep<T>[]; prev: FeedStep<T>[] } {
  const type = effectiveType(current);
  const groups = buildFeedGroups(index, categoryMaster, type);
  const entries = groups.flatMap((group) =>
    group.items.map((item) => ({ item, group }))
  );
  const total = entries.length;
  if (!total || limit <= 0) return { next: [], prev: [] };

  const currentKey = categoryKey(current.category);
  const currentGroup = groups.find((group) => group.key === currentKey);
  const fromLabel = resolveFromLabel(groups, categoryMaster, current);

  const at = entries.findIndex((entry) => entry.item.id === current.id);
  let nextStart: number;
  let prevStart: number;
  if (at >= 0) {
    nextStart = at + 1;
    prevStart = at - 1;
  } else {
    const groupStart = currentGroup
      ? entries.findIndex((entry) => entry.group === currentGroup)
      : 0;
    nextStart = groupStart;
    prevStart = groupStart - 1;
  }

  const walk = (direction: FeedDirection): FeedStep<T>[] => {
    const out: FeedStep<T>[] = [];
    const seen = new Set<string>([current.id]);
    for (let step = 0; step < total && out.length < limit; step += 1) {
      const raw = direction === "next" ? nextStart + step : prevStart - step;
      const position = ((raw % total) + total) % total;
      const entry = entries[position];
      if (seen.has(entry.item.id)) continue;
      seen.add(entry.item.id);
      out.push({
        item: entry.item,
        direction,
        type,
        fromLabel,
        toLabel: entry.group.label,
        switchesCategory: entry.group.key !== currentKey,
        restarts: direction === "next" ? raw >= total : raw < 0,
        context: null,
        contextLabel: null,
        leavesContext: false,
      });
    }
    return out;
  };

  return { next: walk("next"), prev: walk("prev") };
}

/** Daftar asal yang sudah diresolusi server: id berurut + namanya. */
export type FeedContextList = {
  context: FeedContext;
  label: string;
  /** Urutan daftar asal apa adanya (mis. urutan rail / relevansi pencarian). */
  ids: readonly string[];
};

/**
 * Tetangga `current` DI DALAM daftar asal pembeli (`?dari=`): urutan daftar
 * itu, disaring ke produk discoverable bertipe sama. Daftar TIDAK berputar —
 * di ujungnya langkah berikutnya keluar ke feed kategori (`leavesContext`),
 * memakai kandidat `productFeedNeighbors` yang belum tampil.
 *
 * `current` tak ada di daftar (tautan lama, produk berganti tipe, daftar
 * berubah) → `null`: pemanggil jatuh ke feed kategori biasa.
 */
export function productFeedNeighborsInContext<T extends HomeCatalogItem>(
  index: readonly T[],
  categoryMaster: readonly string[],
  current: FeedCurrent,
  list: FeedContextList,
  limit: number = FEED_CANDIDATES
): { next: FeedStep<T>[]; prev: FeedStep<T>[] } | null {
  const type = effectiveType(current);
  const byId = new Map(index.map((item) => [item.id, item]));
  const members: T[] = [];
  const memberIds = new Set<string>();
  for (const id of list.ids) {
    const item = byId.get(id);
    if (!item || memberIds.has(id)) continue;
    if (!isDiscoverableProduct(item) || effectiveType(item) !== type) continue;
    memberIds.add(id);
    members.push(item);
  }
  const at = members.findIndex((item) => item.id === current.id);
  if (at < 0 || limit <= 0) return at < 0 ? null : { next: [], prev: [] };

  const groups = buildFeedGroups(index, categoryMaster, type);
  const labelByKey = new Map(groups.map((group) => [group.key, group.label]));
  const currentKey = categoryKey(current.category);
  const fromLabel = resolveFromLabel(groups, categoryMaster, current);
  // Cadangan untuk keluar dari daftar: ≤ `limit` langkah di dalam daftar bisa
  // kembar dengan kandidat feed kategori, jadi ambil dua kali lipat.
  const fallback = productFeedNeighbors(index, categoryMaster, current, limit * 2);

  const walk = (direction: FeedDirection): FeedStep<T>[] => {
    const out: FeedStep<T>[] = [];
    const seen = new Set<string>([current.id]);
    const inside = direction === "next" ? members.slice(at + 1) : members.slice(0, at).reverse();
    for (const item of inside) {
      if (out.length >= limit) break;
      seen.add(item.id);
      const key = categoryKey(item.category);
      out.push({
        item,
        direction,
        type,
        fromLabel,
        toLabel: labelByKey.get(key) ?? null,
        switchesCategory: key !== currentKey,
        restarts: false,
        context: list.context,
        contextLabel: list.label,
        leavesContext: false,
      });
    }
    for (const step of direction === "next" ? fallback.next : fallback.prev) {
      if (out.length >= limit) break;
      if (seen.has(step.item.id)) continue;
      seen.add(step.item.id);
      out.push({ ...step, contextLabel: list.label, leavesContext: true });
    }
    return out;
  };

  return { next: walk("next"), prev: walk("prev") };
}

const TYPE_LABEL: Record<HomeProductType, string> = {
  preorder: "Pre-Order",
  ready_stock: "Ready Stock",
};

/** Kalimat di slide pratinjau: ke mana geseran ini membawa pembeli. */
export function feedPeekLabel(meta: FeedStepMeta): string {
  const type = TYPE_LABEL[meta.type];
  if (meta.context && meta.contextLabel) {
    return meta.direction === "next"
      ? `Berikutnya di ${meta.contextLabel}`
      : `Sebelumnya di ${meta.contextLabel}`;
  }
  if (meta.leavesContext && meta.contextLabel) {
    const to = meta.toLabel ?? "produk lainnya";
    return meta.direction === "next"
      ? `Semua produk ${meta.contextLabel} sudah dilihat · lanjut ke ${to}`
      : `Awal ${meta.contextLabel} · kembali ke ${to}`;
  }
  if (meta.direction === "next") {
    if (meta.restarts) return `Semua produk ${type} sudah dilihat · mulai lagi dari awal`;
    if (meta.switchesCategory) {
      if (meta.fromLabel && meta.toLabel) {
        return `${meta.fromLabel} sudah habis · lanjut ke ${meta.toLabel}`;
      }
      if (meta.toLabel) return `Lanjut ke kategori ${meta.toLabel}`;
      return "Lanjut ke produk lainnya";
    }
    return meta.toLabel ? `Berikutnya di ${meta.toLabel}` : "Produk berikutnya";
  }
  if (meta.restarts) return `Produk ${type} paling akhir`;
  if (meta.switchesCategory) {
    return meta.toLabel ? `Kembali ke kategori ${meta.toLabel}` : "Kembali ke produk lainnya";
  }
  return meta.toLabel ? `Sebelumnya di ${meta.toLabel}` : "Produk sebelumnya";
}

/**
 * Alamat produk: tujuan feed geser DAN tautan kartu di halaman daftar. Mode
 * katalog reseller WAJIB tetap di dalam `/toko/<kode>` (white-label) — bentuk
 * path-nya sama persis dengan kartu produk, supaya entri cache router yang
 * sama terpakai. `context` = daftar asal (`?dari=`) yang dibawa ke halaman
 * produk supaya feed-nya berjalan di dalam daftar itu.
 */
export function productFeedHref(
  slug: string,
  opts: { catalogKode?: string | null; context?: FeedContext | null } = {}
): string {
  const path = opts.catalogKode
    ? buildCatalogProductPath(opts.catalogKode, slug)
    : `/product/${slug}`;
  return withFeedContext(path, opts.context);
}
